"use client";

import type { Route } from "next";
import Link from "next/link";
import { useEffect, useState } from "react";

/**
 * Turns the missing part of a /blog URL into search words, for example
 * "/blog/o-level-dates-2026" into "o level dates 2026". Returns null when the
 * path is not a blog article path or has no usable words.
 */
export function blogSearchWordsFromPath(pathname: string): string | null {
  if (!pathname.startsWith("/blog/")) return null;
  const last = pathname.split("/").filter(Boolean).at(-1) ?? "";
  let decoded: string;
  try {
    decoded = decodeURIComponent(last);
  } catch {
    return null;
  }
  const words = decoded
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[-_+.]+/g, " ")
    .replace(/[^\p{L}\p{N} ]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80)
    .trim();
  return words.length >= 3 ? words : null;
}

/**
 * Offers a blog search for the words in a missing /blog URL. The not-found
 * page is reached through a middleware rewrite, so the visitor's URL is only
 * reliable in the browser; reading it after mount avoids a hydration mismatch.
 */
export default function BlogNotFoundRecovery() {
  const [words, setWords] = useState<string | null>(null);

  useEffect(() => {
    setWords(blogSearchWordsFromPath(window.location.pathname));
  }, []);

  if (!words) return null;

  const href = `/blog?q=${encodeURIComponent(words)}` as Route;
  return (
    <p className="mt-6">
      <Link<string>
        href={href}
        prefetch={false}
        className="text-blue-600 underline"
        data-ga-action="not_found_blog_search"
      >
        Search the blog for &ldquo;{words}&rdquo;
      </Link>
    </p>
  );
}
